import { transformKeysToCamel } from '../utils/caseTransforms';

// --- Interfaces & Types ---

export interface ApiErrorData {
  status: number;
  errors: Record<string, string[]>;
}

export interface JsonApiErrorSource {
  pointer?: string;
  parameter?: string;
}

export interface JsonApiError {
  status: string;
  code?: string;
  title?: string;
  detail?: string;
  source?: JsonApiErrorSource;
}

// Type guard to check if an object is a valid Rails error structure
function isRailsErrorStructure(errors: any): errors is Record<string, string[]> {
    return typeof errors === 'object' && errors !== null && !Array.isArray(errors) &&
           Object.values(errors).every(value => Array.isArray(value) && value.every(item => typeof item === 'string'));
}

// --- Custom Error Class ---

export class StructuredApiError extends Error implements ApiErrorData {
  status: number;
  errors: Record<string, string[]>;
  originalData?: any;

  constructor(status: number, errors: Record<string, string[]>, message?: string, originalData?: any) {
    // Use the first 'base' error for the main Error message if available
    const baseErrorMessage = errors?.base?.[0] || message || `API Error: Status ${status}`;
    super(baseErrorMessage);
    this.name = 'StructuredApiError';
    this.status = status;
    // Ensure errors is always an object, defaulting to a base error if input is invalid
    this.errors = errors && typeof errors === 'object' ? errors : { base: ['An unknown error occurred'] };
    this.originalData = originalData;

    // Maintain stack trace
    if (Error.captureStackTrace) {
      Error.captureStackTrace(this, StructuredApiError);
    }
  }

  /**
   * Gets the first error message associated with the 'base' key,
   * which typically represents non-field-specific errors.
   * @returns The base error message string, or null if none exists.
   */
  getBaseError(): string | null {
    return this.errors?.base?.[0] || null;
  }

  /**
   * Gets the first error message for a specific field.
   * @param fieldName The name of the field (e.g., 'email', 'password').
   * @returns The error message string, or null if none exists for that field.
   */
  getFieldError(fieldName: string): string | null {
    return this.errors?.[fieldName]?.[0] || null;
  }
}

// --- Parsing Functions ---

// Helper to parse JSON:API formatted errors
const parseJsonApiErrors = (jsonErrors: JsonApiError[]): Record<string, string[]> => {
  const errors: Record<string, string[]> = {};

  jsonErrors.forEach(error => {
    // Prefer pointer for field name, fallback to parameter, default to 'base'
    const field = error.source?.pointer?.split('/').pop() || error.source?.parameter || 'base';
    const message = error.detail || error.title || 'An error occurred';
    if (!errors[field]) {
      errors[field] = [];
    }
    errors[field].push(message);
  });

  // If parsing resulted in no specific errors, return a generic base error
  if (Object.keys(errors).length === 0) {
    return { base: ['An unexpected error occurred during error parsing'] };
  }
  // Consider transforming keys to camelCase here if needed
  // return transformKeysToCamel(errors) as Record<string, string[]>;
  return errors;
};

/**
 * Parses the error data from a failed API response.
 * Prioritizes Rails 422 format, falls back to JSON:API, then generic messages.
 * @param status The HTTP status code.
 * @param jsonData The parsed JSON body from the response.
 * @returns A record mapping field names (or 'base') to error message arrays.
 */
export const parseErrorResponse = (status: number, jsonData: any): Record<string, string[]> => {
  // Default error if no specific format is matched
  let parsedErrors: Record<string, string[]> = { base: [`Request failed with status ${status}`] };

  if (jsonData?.errors) {
    // Check for Rails 422 validation error format first
    if (status === 422 && isRailsErrorStructure(jsonData.errors)) {
      // Transform keys if necessary (e.g., snake_case from Rails to camelCase for JS)
      parsedErrors = transformKeysToCamel(jsonData.errors) as Record<string, string[]>;
    } else if (Array.isArray(jsonData.errors) && jsonData.errors.length > 0) {
      // Check if it's an array of strings (like our promotion API)
      if (jsonData.errors.every((item: any) => typeof item === 'string')) {
        // Handle array of string errors
        parsedErrors = { base: jsonData.errors };
      } else {
        // Fallback to parsing as JSON:API errors array
        parsedErrors = parseJsonApiErrors(jsonData.errors);
      }
    } else if (typeof jsonData.errors === 'string') {
       // Handle cases like { errors: "Some error string" }
       parsedErrors = { base: [jsonData.errors] };
    }
    // If jsonData.errors is something else (e.g., empty array/object), parsedErrors retains the default
  } else if (jsonData?.error && typeof jsonData.error === 'string') {
    // Handle generic { error: "..." } (singular) common in simple API errors
    parsedErrors = { base: [jsonData.error] };
  } else if (jsonData?.message && typeof jsonData.message === 'string') {
    // Handle generic { message: "..." } errors
    parsedErrors = { base: [jsonData.message] };
  } else if (typeof jsonData === 'string') {
    // Handle cases where the error response is just a plain string
    parsedErrors = { base: [jsonData] };
  }

  // Ensure there's always at least a base error if parsing failed to produce anything specific
  if (Object.keys(parsedErrors).length === 0 || (parsedErrors.base && parsedErrors.base.length === 0 && Object.keys(parsedErrors).length === 1)) {
     parsedErrors = { base: [`Request failed with status ${status}`] };
  }


  return parsedErrors;
};
